import { Mail, Linkedin, Download } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/Reveal";

export const Media = () => {
  const { t } = useLanguage();
  const m = t.mediaPage;
  const pr = t.footer.prMedia;

  return (
    <section id="media" className="relative py-24 md:py-32 bg-[#010E3A] border-y border-white/5">
      <div className="container-page">
        <Reveal className="max-w-3xl">
          <span className="overline text-[#CEA26B]">{m.overline}</span>
          <h2 className="mt-4 font-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {m.title}
          </h2>
          <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed">{m.intro}</p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <h3 className="font-display font-bold text-white text-2xl tracking-tight">{m.factsTitle}</h3>
            <dl className="mt-6 space-y-px bg-white/10 border border-white/10">
              {m.facts.map((fact, i) => (
                <div
                  key={i}
                  data-testid={`media-fact-${i}`}
                  className="bg-[#010E3A] px-5 py-4 flex flex-col sm:flex-row sm:gap-6"
                >
                  <dt className="overline text-slate-500 sm:w-36 shrink-0">{fact.label}</dt>
                  <dd className="mt-1 sm:mt-0 text-base text-slate-200 leading-relaxed">{fact.value}</dd>
                </div>
              ))}
            </dl>
          </Reveal>

          <Reveal delay={0.08}>
            <h3 className="font-display font-bold text-white text-2xl tracking-tight">{m.contactTitle}</h3>
            <div className="mt-6 border border-[#CEA26B]/30 bg-[#CEA26B]/[0.06] p-7">
              <p className="font-display font-bold text-white text-lg">{m.contactName}</p>
              <p className="mt-1 text-sm text-[#CEA26B]">{m.contactRole}</p>
              <a
                href={`mailto:${m.contactEmail}`}
                data-testid="media-contact-email"
                className="mt-4 inline-flex items-center gap-2.5 text-sm text-slate-200 hover:text-white transition-colors"
              >
                <Mail className="h-4 w-4 text-[#CEA26B]" /> {m.contactEmail}
              </a>
              <div className="flex items-center gap-4 mt-3 text-sm">
                <a
                  href={pr.linkedinSybaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors"
                  data-testid="media-linkedin-syba"
                >
                  <Linkedin className="h-4 w-4 text-[#CEA26B]" /> SYBA
                </a>
                <a
                  href={pr.linkedinObalRokuUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors"
                  data-testid="media-linkedin-obalroku"
                >
                  <Linkedin className="h-4 w-4 text-[#CEA26B]" /> Obal roku
                </a>
              </div>
            </div>

            <div className="mt-8">
              <h4 className="font-display font-bold text-white text-lg">{m.assetsTitle}</h4>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                {m.assetsNote}{" "}
                <a href={`mailto:${m.contactEmail}`} className="text-[#CEA26B] hover:underline">
                  {m.contactEmail}
                </a>
                .
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16">
            <h3 className="font-display font-bold text-white text-2xl tracking-tight" data-testid="media-downloads-title">
              {m.downloadsTitle}
            </h3>
            <p className="mt-3 text-base text-slate-400 leading-relaxed max-w-2xl">{m.downloadsNote}</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {m.downloads.map((d, i) => (
                <a
                  key={i}
                  href={d.file}
                  download
                  data-testid={`media-download-${i}`}
                  className="group flex items-center gap-4 bg-[#010E3A] px-5 py-4 hover:bg-[#CEA26B]/[0.08] transition-colors"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-[#CEA26B]/40 text-[#CEA26B] group-hover:bg-[#CEA26B] group-hover:text-[#01154F] transition-colors">
                    <Download className="h-4 w-4" />
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm font-semibold text-white leading-snug">{d.label}</span>
                    <span className="mt-0.5 block text-xs text-slate-500 tracking-wide">{d.meta}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16 max-w-3xl">
            <h3 className="font-display font-bold text-white text-2xl tracking-tight">{m.boilerplateTitle}</h3>
            <p
              data-testid="media-boilerplate"
              className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed border-l-2 border-[#CEA26B] pl-6"
            >
              {m.boilerplate}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
